import {useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore} from 'react';
import {getSessionFirmId, getSessionUserId} from '../lib/picos/sessionIdentity';
import {tabSession} from '../Auth/session';

// Define the enum for storage types
export enum LocalStorageType {
    string = 'string',
    number = 'number',
    boolean = 'boolean',
    object = 'object',
    array = 'array',
    json = 'json',
}

export enum KeySpecificity {
    None = 0,
    FirmSpecific = 1,
    UserSpecific = 2,
}

// Utility type to map LocalStorageType to TypeScript types
type StorageTypeMap<T extends LocalStorageType> =
    T extends LocalStorageType.string ? string :
    T extends LocalStorageType.number ? number :
    T extends LocalStorageType.boolean ? boolean :
    T extends LocalStorageType.object ? Record<string, any> :
    T extends LocalStorageType.array ? any[] :
    T extends LocalStorageType.json ? any :
    never;

// Helper functions for serialization/deserialization based on type
const serialize = <T>(value: T, type: LocalStorageType): string => {
    switch (type) {
        case LocalStorageType.string:
            return value as unknown as string;
        case LocalStorageType.number:
            return value?.toString() || '';
        case LocalStorageType.boolean:
            return (value as unknown as boolean) ? 'true' : 'false';
        case LocalStorageType.object:
        case LocalStorageType.array:
        case LocalStorageType.json:
        default:
            return JSON.stringify(value);
    }
};

const deserialize = <T>(value: string | null, type: LocalStorageType, defaultValue?: T): T | undefined => {
    if (value === null) return defaultValue;

    switch (type) {
        case LocalStorageType.string:
            return value as unknown as T;
        case LocalStorageType.number:
            return Number(value) as unknown as T;
        case LocalStorageType.boolean:
            return (value === 'true') as unknown as T;
        case LocalStorageType.object:
        case LocalStorageType.array:
        case LocalStorageType.json:
        default:
            return JSON.parse(value);
    }
};

// Account preferences require the full validated tab owner, including callers
// using the historical FirmSpecific option. Global preferences remain explicit.
function preferenceKey(key: string, specificity: KeySpecificity): string | undefined {
    if (specificity === KeySpecificity.None) return key;
    const firmId = getSessionFirmId();
    const userId = getSessionUserId();
    return firmId && userId ? `${key}-${firmId}-${userId}` : undefined;
}

// Custom hook for syncing state with localStorage.
export function useLocalStorage<E extends LocalStorageType, T = StorageTypeMap<E>>(
    originalKey: string,
    type: E,
    initialValue?: T,
    userSpecificKey: KeySpecificity = KeySpecificity.None
): [T | undefined, (value: T | ((val: T | undefined) => T)) => void] {
    const revision = useSyncExternalStore(tabSession.subscribe, tabSession.getRevision);
    // captureGeneration reads external mutable state; a revision deliberately
    // replaces the setter's captured operation even when the account key is unchanged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const operation = useMemo(() => tabSession.captureGeneration(), [revision]);
    const key = preferenceKey(originalKey, userSpecificKey);
    const initial = useRef(initialValue);
    initial.current = initialValue;

    const read = useCallback((): T | undefined => {
        if (!key || typeof window === 'undefined') return initial.current;
        try {
            return deserialize<T>(window.localStorage.getItem(key), type, initial.current);
        } catch (error) {
            console.error(`Error reading localStorage key "${key}"`, error);
            return initial.current;
        }
    }, [key, type]);
    const [stored, setStored] = useState(() => ({key, value: read()}));
    // Never expose the previous account's value for even one render.
    const value = stored.key === key ? stored.value : read();
    const current = useRef({key, value});
    current.current = {key, value};

    useEffect(() => { setStored({key, value: read()}); }, [key, read]);

    const isCurrent = useCallback(() => userSpecificKey === KeySpecificity.None ||
        (!operation.signal.aborted && key === preferenceKey(originalKey, userSpecificKey)),
    [key, operation, originalKey, userSpecificKey]);

    const setValue = useCallback((update: T | ((val: T | undefined) => T)) => {
        if (!key || !isCurrent()) return;
        try {
            const previous = current.current.key === key ? current.current.value : read();
            const next = update instanceof Function ? update(previous) : update;
            if (next === undefined) window.localStorage.removeItem(key);
            else window.localStorage.setItem(key, serialize(next, type));
            current.current = {key, value: next};
            setStored({key, value: next});
            window.dispatchEvent(new CustomEvent('localStorage', {detail: {key, value: next, type}}));
        } catch (error) {
            console.error(`Error setting localStorage key "${key}"`, error);
        }
    }, [key, type, read, isCurrent]);

    useEffect(() => {
        if (!key || typeof window === 'undefined') return;
        const refresh = () => {
            if (!isCurrent()) return;
            const next = read();
            current.current = {key, value: next};
            setStored({key, value: next});
        };
        const handleStorage = (event: StorageEvent) => {
            if (event.key === key || event.key === null) refresh();
        };
        const handleCustom = (event: Event) => {
            if ((event as CustomEvent).detail?.key === key) refresh();
        };
        window.addEventListener('storage', handleStorage);
        window.addEventListener('localStorage', handleCustom);
        return () => {
            window.removeEventListener('storage', handleStorage);
            window.removeEventListener('localStorage', handleCustom);
        };
    }, [key, read, isCurrent]);

    return [value, setValue];
}
